import { type GSASearchResultsDTO } from "~/features/selectionAssistant/gearbox/dtos";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { type GSAPageState } from "~/features/selectionAssistant/gearbox/useGearboxSALogic";
import { strictIsNonNull } from "~/common/utils/reducers";

export const GSA_SEARCH_RESULTS_URL = resolveApiUrl("/search/pagedata");

export async function fetchGSASearchResults(
  params: URLSearchParams,
  abortSignal: AbortSignal,
): Promise<GSASearchResultsDTO> {
  const response = await fetch(GSA_SEARCH_RESULTS_URL + "?" + params.toString(), {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    signal: abortSignal,
  });

  if (response.redirected) {
    window.location.reload();
  }

  if (!response.ok) {
    throw new Error("Error fetching search results");
  }

  return response.json();
}

export function generateFetchParams({
  characteristicValues,
  dimensionValues,
  dimensionDefinitions,
  selectedSort,
  selectedSortDirection,
  currentPage,
  pageSize,
}: Pick<
  GSAPageState,
  | "characteristicValues"
  | "dimensionValues"
  | "dimensionDefinitions"
  | "selectedSort"
  | "selectedSortDirection"
  | "currentPage"
  | "pageSize"
>) {
  const params = new URLSearchParams();

  const dimensionParts: string[] = [];
  Object.entries(dimensionValues).forEach(([key, value]) => {
    const definition = dimensionDefinitions[key];
    const [minVal, maxVal] = value ?? [];
    if (strictIsNonNull(minVal) && minVal > definition.min) {
      dimensionParts.push(`${key}_min:${minVal}`);
    }
    if (strictIsNonNull(maxVal) && maxVal < definition.max) {
      dimensionParts.push(`${key}_max:${maxVal}`);
    }
  });
  if (dimensionParts.length > 0) {
    params.set("techValues", dimensionParts.join(","));
  }

  const selectedSortCode = [selectedSort, selectedSortDirection].filter(Boolean).join("");

  const qParts: string[] = [];
  qParts.push(""); // No search string
  qParts.push(selectedSortCode); // Sort by relevance
  Object.entries(characteristicValues).forEach(([key, value]) => {
    (Array.isArray(value) ? value : [value]).forEach((v) => {
      if (v) {
        qParts.push(`${key}:${v}`);
      }
    });
  });
  params.set("q", qParts.join(":"));

  params.set("page", currentPage.toString());

  if (pageSize) {
    params.set("pageSize", pageSize.toString());
  }

  // Always set GEARBOX as isSelectionAssistant
  params.set("isSelectionAssistant", "GEARBOX");

  return params;
}
